import { useState, useEffect, useCallback } from 'react';
import {
  Plus, X, Loader2, CalendarDays, Check, XCircle, Trash2,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';

interface LeaveRequest {
  id: string;
  type: string;
  startDate: string;
  endDate: string;
  days: number;
  reason?: string;
  status: string;
  employee?: { id: string; firstName: string; lastName: string; employeeCode: string } | null;
  createdAt: string;
}

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  employeeCode: string;
}

const LEAVE_TYPES = ['casual', 'sick', 'earned', 'unpaid'];
const STATUSES = ['pending', 'approved', 'rejected'];

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-50 text-yellow-700',
  approved: 'bg-green-50 text-green-700',
  rejected: 'bg-red-50 text-red-700',
};

const TYPE_LABELS: Record<string, string> = {
  casual: 'Casual Leave',
  sick: 'Sick Leave',
  earned: 'Earned Leave',
  unpaid: 'Unpaid Leave',
};

export function LeavePage() {
  const [leaves, setLeaves] = useState<LeaveRequest[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    employeeId: '',
    type: 'casual',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    days: '1',
    reason: '',
  });

  const fetchLeaves = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {};
      if (filterStatus) params.status = filterStatus;
      const res = await api.get('/leave-requests', { params });
      setLeaves(res.data.data ?? []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchLeaves();
    // 2026-10-08: one page is 20 people; ask for the API's maximum (100) so the picker lists everyone.
    api.get('/employees', { params: { limit: 100 } }).then(res => {
      setEmployees(res.data.data?.employees ?? res.data.data ?? []);
    }).catch(() => {});
  }, [fetchLeaves]);

  const handleCreate = async () => {
    try {
      setSaving(true);
      await api.post('/leave-requests', {
        employeeId: form.employeeId,
        type: form.type,
        startDate: form.startDate,
        endDate: form.endDate,
        days: parseFloat(form.days) || 1,
        reason: form.reason || undefined,
      });
      setShowModal(false);
      setForm({ employeeId: '', type: 'casual', startDate: new Date().toISOString().split('T')[0], endDate: '', days: '1', reason: '' });
      fetchLeaves();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create leave request');
    } finally {
      setSaving(false);
    }
  };

  const handleApproveReject = async (id: string, action: 'approved' | 'rejected') => {
    try {
      await api.patch(`/leave-requests/${id}/approve`, { action });
      fetchLeaves();
    } catch (err: any) {
      alert(err.response?.data?.error || `Failed to ${action === 'approved' ? 'approve' : 'reject'}`);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this leave request?')) return;
    try {
      await api.delete(`/leave-requests/${id}`);
      fetchLeaves();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete');
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between max-sm:flex-wrap max-sm:gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leave Requests</h1>
          <p className="text-sm text-gray-500">Manage employee leave applications</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors max-sm:min-h-[40px] max-sm:whitespace-nowrap"
        >
          <Plus size={16} />
          New Leave Request
        </button>
      </div>

      {/* Filters */}
      <div className="mb-4 flex gap-3">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none max-sm:min-h-[40px]"
        >
          <option value="">All Statuses</option>
          {STATUSES.map(s => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
            <span className="ml-2 text-sm text-gray-500">Loading...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-red-500">
            <p className="text-sm">{error}</p>
            <button onClick={fetchLeaves} className="mt-2 text-sm text-[var(--ink-0)] hover:underline max-sm:min-h-[40px]">Retry</button>
          </div>
        ) : leaves.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <CalendarDays size={40} className="mb-2" />
            <p className="text-sm">No leave requests found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3 max-sm:sticky max-sm:left-0 max-sm:z-10 max-sm:bg-gray-50 max-sm:shadow-[1px_0_0_#e5e7eb]">Employee</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Start Date</th>
                  <th className="px-4 py-3">End Date</th>
                  <th className="px-4 py-3 text-center">Days</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {leaves.map(leave => (
                  <tr key={leave.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-900 max-sm:sticky max-sm:left-0 max-sm:z-10 max-sm:min-w-[104px] max-sm:bg-white max-sm:shadow-[1px_0_0_#e5e7eb]">
                      {leave.employee ? `${leave.employee.firstName} ${leave.employee.lastName}` : '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-sm:whitespace-nowrap">{TYPE_LABELS[leave.type] || leave.type}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(leave.startDate)}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(leave.endDate)}</td>
                    <td className="px-4 py-3 text-center text-gray-900 font-medium">{Number(leave.days)}</td>
                    <td className="px-4 py-3 text-gray-600 max-w-48 truncate">{leave.reason || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[leave.status] || STATUS_COLORS.pending}`}>
                        {leave.status.charAt(0).toUpperCase() + leave.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {leave.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApproveReject(leave.id, 'approved')}
                              className="rounded p-1 text-gray-400 hover:bg-green-50 hover:text-green-600 max-sm:flex max-sm:min-h-[40px] max-sm:min-w-[40px] max-sm:items-center max-sm:justify-center"
                              title="Approve"
                            >
                              <Check size={15} />
                            </button>
                            <button
                              onClick={() => handleApproveReject(leave.id, 'rejected')}
                              className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 max-sm:flex max-sm:min-h-[40px] max-sm:min-w-[40px] max-sm:items-center max-sm:justify-center"
                              title="Reject"
                            >
                              <XCircle size={15} />
                            </button>
                          </>
                        )}
                        {leave.status === 'pending' && (
                          <button
                            onClick={() => handleDelete(leave.id)}
                            className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 max-sm:flex max-sm:min-h-[40px] max-sm:min-w-[40px] max-sm:items-center max-sm:justify-center"
                            title="Delete"
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Leave Request Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowModal(false)}>
          <div className="mx-4 w-full max-w-lg rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto max-sm:mx-0 max-sm:flex max-sm:h-full max-sm:max-h-none max-sm:max-w-none max-sm:flex-col max-sm:rounded-none" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl max-sm:rounded-none max-sm:px-4">
              <h2 className="text-lg font-semibold text-gray-900">New Leave Request</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 max-sm:-mr-2 max-sm:flex max-sm:min-h-[40px] max-sm:min-w-[40px] max-sm:items-center max-sm:justify-center"><X size={20} /></button>
            </div>
            <div className="space-y-4 px-6 py-4 max-sm:px-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Employee *</label>
                <select
                  value={form.employeeId}
                  onChange={(e) => setForm({ ...form, employeeId: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none max-sm:min-h-[40px]"
                >
                  <option value="">Select Employee</option>
                  {employees.map(e => (
                    <option key={e.id} value={e.id}>{e.firstName} {e.lastName}{e.employeeCode ? ` (${e.employeeCode})` : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Leave Type *</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none max-sm:min-h-[40px]"
                >
                  {LEAVE_TYPES.map(t => (
                    <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-4 max-sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Start Date *</label>
                  <input type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)] max-sm:min-h-[40px]" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">End Date *</label>
                  <input type="date" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)] max-sm:min-h-[40px]" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Days *</label>
                  <input type="number" min="0.5" step="0.5" value={form.days} onChange={e => setForm({ ...form, days: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)] max-sm:min-h-[40px]" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Reason</label>
                <textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} rows={2}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)] max-sm:min-h-[40px]" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 max-sm:mt-auto max-sm:px-4">
              <button onClick={() => setShowModal(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 max-sm:min-h-[44px]">Cancel</button>
              <button
                onClick={handleCreate}
                disabled={saving || !form.employeeId || !form.startDate || !form.endDate}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50 max-sm:min-h-[44px]"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
